import React, { useEffect } from "react";
import CountUp from "react-countup";

const Counts = () => {
    // For animation on scroll
    const [isVisible, setIsVisible] = React.useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        const element = document.getElementById('stats');
        if (element) observer.observe(element);

        return () => {
            if (element) observer.unobserve(element);
        };
    }, []);

    const statsData = [
        {
            id: 1,
            icon: "bi-journal-text",
            title: "Past Questions",
            hint: "Verified exam papers & answers",
            value: 20,
            suffix: "+",
            duration: 2.5,
            color: "primary",
            delay: 150
        },
        {
            id: 2,
            icon: "bi-mortarboard",
            title: "Active Students",
            hint: "Using our platform monthly",
            value: 300,
            suffix: "+",
            duration: 3,
            color: "success",
            delay: 200
        },
        {
            id: 3,
            icon: "bi-clock-history",
            title: "Years Coverage",
            hint: "Exam papers from past years",
            value: 5,
            suffix: "+",
            duration: 2,
            color: "warning",
            delay: 250
        },
        {
            id: 4,
            icon: "bi-check-circle",
            title: "Accuracy Rate",
            hint: "Verified by subject experts",
            value: 99.5,
            suffix: "%",
            duration: 3.5,
            color: "info",
            delay: 300
        }
    ];

    const educationLevels = [
        {
            id: 1,
            name: "BECE",
            count: 20,
            color: "var(--bs-primary)",
            icon: "bi-building"
        },
        {
            id: 2,
            name: "SHS/WASSCE",
            count: 50,
            color: "var(--bs-success)",
            icon: "bi-book"
        },
        {
            id: 3,
            name: "CTVET",
            count: 20,
            color: "var(--bs-warning)",
            icon: "bi-gear"
        },
        {
            id: 4,
            name: "University",
            count: 50,
            color: "var(--bs-info)",
            icon: "bi-mortarboard"
        }
    ];

    return (
        <section id="stats" className="stats section bg-light">
            <div className="container" data-aos="fade-up" data-aos-delay="100">
                {/* Section Header */}
                <div className="row justify-content-center mb-5">
                    <div className="col-lg-8 text-center">
                        <div className="section-header">
                            <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2 mb-3 d-inline-flex align-items-center">
                                <i className="bi bi-bar-chart me-2"></i>
                                Platform Statistics
                            </span>
                            <h2 className="display-5 fw-bold mb-3">
                                Trusted by Thousands of Students
                            </h2>
                            <p className="lead text-muted mb-0">
                                Our growing database helps students across Ghana excel in their exams
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Stats Grid */}
                <div className="row justify-content-center">
                    <div className="col-lg-11">
                        <div className="stats-board bg-white rounded-4 shadow-sm p-4 p-lg-5">
                            <div className="row g-4">
                                {statsData.map((stat) => (
                                    <div key={stat.id} className="col-md-6 col-lg-3">
                                        <article 
                                            className="stat-tile h-100 p-4 rounded-3 border-0"
                                            data-aos="fade-up" 
                                            data-aos-delay={stat.delay}
                                            style={{
                                                background: `linear-gradient(135deg, var(--bs-${stat.color}-bg-subtle) 0%, rgba(var(--bs-${stat.color}-rgb), 0.05) 100%)`,
                                                border: `1px solid rgba(var(--bs-${stat.color}-rgb), 0.1)`
                                            }}
                                        >
                                            <div className="tile-head d-flex align-items-center mb-3">
                                                <div className={`icon-wrapper d-flex align-items-center justify-content-center rounded-circle me-3`}
                                                     style={{
                                                         width: '48px',
                                                         height: '48px',
                                                         background: `rgba(var(--bs-${stat.color}-rgb), 0.1)`,
                                                         color: `var(--bs-${stat.color})`
                                                     }}>
                                                    <i className={`bi ${stat.icon} fs-4`}></i>
                                                </div>
                                                <div className="label">
                                                    <h6 className="title fw-semibold mb-1">{stat.title}</h6>
                                                    <small className="hint text-muted">{stat.hint}</small>
                                                </div>
                                            </div>
                                            <div className="tile-metric">
                                                {isVisible && (
                                                    <div className="d-flex align-items-baseline">
                                                        <span className="metric-number display-5 fw-bold" style={{ color: `var(--bs-${stat.color})` }}>
                                                            <CountUp
                                                                end={stat.value}
                                                                duration={stat.duration}
                                                                separator=","
                                                                decimals={stat.value % 1 !== 0 ? 1 : 0}
                                                            />
                                                        </span>
                                                        <span className="metric-suffix fs-4 fw-semibold ms-1" style={{ color: `var(--bs-${stat.color})` }}>
                                                            {stat.suffix}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </article>
                                    </div>
                                ))}
                            </div>

                            {/* Education Level Distribution */}
                            <div className="row mt-5 pt-4" data-aos="fade-up" data-aos-delay="350">
                                <div className="col-lg-12">
                                    <div className="distribution-card bg-dark bg-opacity-5 rounded-4 p-4">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <div>
                                                <h5 className="fw-bold mb-2 text-white">Questions by Education Level</h5>
                                                <p className="text-muted mb-0">Distribution of past questions across different educational stages</p>
                                            </div>
                                            <span className="badge bg-primary rounded-pill px-3 py-2">
                                                <i className="bi bi-pie-chart me-2"></i>
                                                Live Distribution
                                            </span>
                                        </div>
                                        
                                        <div className="row g-4">
                                            {educationLevels.map((level) => (
                                                <div key={level.id} className="col-md-3 col-sm-6">
                                                    <div className="level-card p-3 rounded-3 bg-white shadow-sm">
                                                        <div className="d-flex align-items-center mb-3">
                                                            <div className="level-icon me-3" 
                                                                 style={{ color: level.color }}>
                                                                <i className={`bi ${level.icon} fs-4`}></i>
                                                            </div>
                                                            <div>
                                                                <h6 className="fw-bold mb-0">{level.name}</h6>
                                                                <div className="progress mt-2" style={{ height: '6px' }}>
                                                                    <div 
                                                                        className="progress-bar rounded-pill" 
                                                                        role="progressbar" 
                                                                        style={{ 
                                                                            width: `${(level.count / 11000) * 100}%`,
                                                                            backgroundColor: level.color
                                                                        }}
                                                                        aria-valuenow={(level.count / 11000) * 100}
                                                                        aria-valuemin="0" 
                                                                        aria-valuemax="100"
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span className="text-muted">Questions</span>
                                                            <span className="fw-bold fs-5">{level.count.toLocaleString()}+</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Legend & Additional Info */}
                            <div className="row mt-4">
                                <div className="col-lg-12">
                                    <div className="legend-row d-flex flex-wrap gap-4 justify-content-center" data-aos="fade-down" data-aos-delay="400">
                                        <div className="legend-item d-flex align-items-center">
                                            <div className="dot dot-primary d-inline-block rounded-circle me-2" 
                                                 style={{ width: '10px', height: '10px', backgroundColor: 'var(--bs-primary)' }}></div>
                                            <span className="text-muted">Data updated monthly</span>
                                        </div>
                                        <div className="legend-item d-flex align-items-center">
                                            <div className="dot dot-success d-inline-block rounded-circle me-2" 
                                                 style={{ width: '10px', height: '10px', backgroundColor: 'var(--bs-success)' }}></div>
                                            <span className="text-muted">Real-time statistics</span>
                                        </div>
                                        <div className="legend-item d-flex align-items-center">
                                            <div className="dot dot-warning d-inline-block rounded-circle me-2" 
                                                 style={{ width: '10px', height: '10px', backgroundColor: 'var(--bs-warning)' }}></div>
                                            <span className="text-muted">Verified by educators</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Call to Action */}
                        <div className="cta-section text-center mt-5" data-aos="fade-up" data-aos-delay="450">
                            <div className="bg-primary bg-opacity-10 rounded-4 p-4 p-lg-5 border border-primary border-opacity-25">
                                <div className="row align-items-center">
                                    <div className="col-lg-8 text-lg-start">
                                        <h4 className="fw-bold mb-2">Join Our Growing Community</h4>
                                        <p className="text-muted mb-0">
                                            Start your exam preparation journey today with access to all our resources
                                        </p>
                                    </div>
                                    <div className="col-lg-4 text-lg-end mt-3 mt-lg-0">
                                        <a href="/questions" className="btn btn-primary btn-lg px-4">
                                            <i className="bi bi-person-plus me-2"></i>
                                            Get Started
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </section>
    );
};

export default Counts;